import { v4 as uuid } from 'uuid';

import { IAppDispatch } from '../../../redux/core-store';
import { setActiveConversationId } from '../../../redux/slices/chat-tab/chat-tab-slice';
import {
    IAutomaticAssistantMessage,
    IConversation,
    IInputEvent,
    ILLMConfig,
    IMessage,
    IMessageEvent,
    IToolCallEvent,
    IToolResultEvent,
    MessageRole,
    MessageStageType,
    StreamEventType,
    StreamStatus,
} from '../../types';

import { CONVERSATION_FUNC, CONVERSATION_GET_MESSAGES_FUNC, MESSAGES_PER_PAGE } from './consts';
import { conversationEndpoints } from './conversation-endpoints';
import { messageEndpoints } from './message-endpoints';

export const getMessagePlaceholder = (messages: IMessage[]): IMessage | undefined => {
    return messages.find((m) => m.is_placeholder);
};

export const getErrorMessage = (messages: IMessage[]): IMessage | undefined => {
    return messages.find((m) => m.is_retryable);
};

export const getLastRunningMessage = (messages: IMessage[]): IMessage | undefined => {
    return messages.find((m) => m.status === StreamStatus.RUNNING);
};

const isObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

export const isAutomaticAssistantMessage = (
    event: unknown,
    eventType?: StreamEventType
): event is IAutomaticAssistantMessage => {
    return isObject(event) && eventType === StreamEventType.AUTOMATIC_ASSISTANT_MESSAGE;
};

export const isToolCallEvent = (event: unknown, eventType?: StreamEventType): event is IToolCallEvent => {
    return isObject(event) && eventType === StreamEventType.TOOL_CALL && 'arguments' in event;
};

export const isInputEvent = (event: unknown, eventType?: StreamEventType): event is IInputEvent => {
    return isObject(event) && eventType === StreamEventType.INPUT && 'raw_input' in event;
};

export const isToolResultEvent = (event: unknown, eventType?: StreamEventType): event is IToolResultEvent => {
    return isObject(event) && eventType === StreamEventType.TOOL_RESULT && 'tool_call_succeeded' in event;
};

export const isMessageEvent = (event: unknown, eventType?: StreamEventType): event is IMessageEvent => {
    return isObject(event) && eventType === StreamEventType.CHUNK && 'message_id' in event;
};

type CreateConversationPlaceholderParams = {
    conversationId: IConversation['conversation_id'];
    messages: IMessage[];
    appId?: IConversation['app_id'];
    appSnapshot?: IConversation['app_snapshot'];
    llmConfig?: ILLMConfig;
};

type GetTurnInitialTurnMessagesParams = {
    turnId: IMessage['turn_id'];
    userInitialMessage?: IMessage['content'];
    appId?: IConversation['app_id'];
    appSnapshot?: IConversation['app_snapshot'];
};

export const getTurnInitialTurnMessages = ({
    turnId,
    userInitialMessage,
    appId,
    appSnapshot,
}: GetTurnInitialTurnMessagesParams): IMessage[] => {
    const messages = [];
    const createdAt = new Date();
    if (userInitialMessage) {
        messages.push({
            message_id: uuid(),
            turn_id: turnId,
            content: userInitialMessage,
            role: MessageRole.USER,
            status: StreamStatus.DONE,
            created_at: createdAt,
        });
    }

    messages.push({
        message_id: uuid(),
        turn_id: turnId,
        content: '',
        is_placeholder: true,
        role: MessageRole.ASSISTANT,
        status: StreamStatus.RUNNING,
        stage: MessageStageType.LOADING,
        app_id: appId,
        app_snapshot: appSnapshot,
        created_at: createdAt,
    });

    return messages;
};

export const getLastConversationId = (conversationHistory: IConversation[]): IConversation['conversation_id'] => {
    return conversationHistory[conversationHistory.length - 1].conversation_id;
};

export const createConversationPlaceholder = (params: CreateConversationPlaceholderParams): IConversation => {
    const { conversationId, messages, appId, appSnapshot, llmConfig } = params;

    const date = new Date().toISOString();

    return {
        title: null,
        conversation_id: conversationId,
        created_at: date,
        updated_at: date,
        deleted_at: null,
        action_id: undefined,
        app_id: appId,
        app_snapshot: appSnapshot,
        messages,
        llm_config: llmConfig,
    };
};

export const createConversation = (
    dispatch: IAppDispatch,
    conversation: IConversation,
    customer_project_id: string
) => {
    const { conversation_id, messages } = conversation;

    // Create a new conversation
    dispatch(
        conversationEndpoints.util.updateQueryData('getAllConversations', { customer_project_id }, (draft) => {
            if (draft.length) {
                draft.unshift(conversation);
            }
        })
    );

    dispatch(
        conversationEndpoints.util.upsertQueryData(
            CONVERSATION_FUNC,
            { customer_project_id, conversation_id },
            conversation
        )
    );

    dispatch(
        messageEndpoints.util.upsertQueryData(
            CONVERSATION_GET_MESSAGES_FUNC,
            { customer_project_id, conversation_id },
            { pages: [{ messages, cursor: null }], pageParams: [{ limit: MESSAGES_PER_PAGE, cursor: undefined }] }
        )
    );

    dispatch(setActiveConversationId(conversation_id));
};
